import {
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  OnInit,
  viewChild,
} from '@angular/core';
import { OrganizationSidebarLayout } from '../../../organizations/components/organization-sidebar-layout/organization-sidebar-layout';
import { PageHeader } from '../../../layout/components/page-header/page-header';
import { PageTitle } from '../../../layout/components/page-title/page-title';
import { Button, SimpleDialog, InputSimple, NotificationService } from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { LocationCreateDialog } from '../../dialogs/location-create-dialog/location-create-dialog';
import { LocationsStore } from '../../store/locations.store';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { FormField, form } from '@angular/forms/signals';
import { LocationsCreateSuccessNotification } from '../../notifications/locations-create-success-notification/locations-create-success-notification';
import { formatLocationName } from '../../pipes/location-name-pipe';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { LocationListEntrySkeleton } from '../../components/location-list-entry-skeleton/location-list-entry-skeleton';
import { LocationsEmptyBox } from '../../components/locations-empty-box/locations-empty-box';
import { LocationsAddBox } from '../../components/locations-add-box/locations-add-box';
import { LocationsList } from '../../components/locations-list/locations-list';
import { TutorialLocationsPopover } from '../../../tutorials/components/tutorial-locations-popover/tutorial-locations-popover';
import { VideoTutorialService } from '../../../tutorials/services/video-tutorial.service';

@Component({
  selector: 'app-locations-page',
  templateUrl: './locations-page.html',
  imports: [
    OrganizationSidebarLayout,
    PageHeader,
    PageTitle,
    Button,
    NgIcon,
    LocationCreateDialog,
    SimpleDialog,
    FormsModule,
    TranslocoPipe,
    InputSimple,
    FormField,
    ErrorPage,
    LocationListEntrySkeleton,
    LocationsEmptyBox,
    LocationsAddBox,
    LocationsList,
    TutorialLocationsPopover,
  ],
})
export class LocationsPage implements OnInit {
  private videoTutorialService = inject(VideoTutorialService);
  private notificationService = inject(NotificationService);
  private locationsStore = inject(LocationsStore);
  private organizationsStore = inject(OrganizationsStore);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  protected organizationId = this.organizationsStore.selectedId;

  private tutorialPopover = viewChild(TutorialLocationsPopover);
  private createLocationDialog = viewChild<SimpleDialog>('createLocationDialog');

  protected locations = computed(() => this.locationsStore.filteredLocations() ?? []);
  protected isLoading = this.locationsStore.isLoading;
  protected error = this.locationsStore.error;

  private filterModel = linkedSignal(() => ({
    query: this.locationsStore.filterQuery(),
  }));
  protected filterForm = form(this.filterModel);

  constructor() {
    effect(() => {
      const query = this.filterForm.query().value() || null;
      const queryParams: Params = { query };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge',
      });
    });
    effect(() => {
      const popover = this.tutorialPopover();
      if (popover && this.videoTutorialService.value()?.LumoTutorialLocations === false) {
        popover.showPopover();
        this.videoTutorialService.markTutorialAsViewed('LumoTutorialLocations');
      }
    });
  }

  ngOnInit(): void {
    this.locationsStore.setFilterQuery(
      this.activatedRoute.snapshot.queryParamMap.get('query') || '',
    );
  }

  protected setFilterQuery(query: string | null) {
    this.locationsStore.setFilterQuery(query ?? '');
  }

  protected onCreateSuccess(locationId: string) {
    this.createLocationDialog()?.close();
    const location = this.locationsStore.locations()?.find((loc) => loc.id === locationId)?.data;
    this.notificationService.show({
      component: LocationsCreateSuccessNotification,
      duration: 5000,
      componentProps: {
        organizationId: this.organizationId(),
        locationId,
        locationName: formatLocationName(location) || undefined,
      },
    });
  }

  protected onClickBack() {
    this.router.navigate(['/organizations']);
  }
}
